import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { loadCompanyNameOnce, resetCompanyName, setPrintOnReceiveDefault, TENANT_FALLBACK } from '../lib/tenant';
import { isInactiveRefusal } from '../lib/signInRefusal';

const AuthContext = createContext({});

// #686, Session 79.22b.4.3 -- THE SAME PROFILE WHEN NOTHING IN IT CHANGED.
//
// The auth library announces "signed in" again every time the page becomes
// visible -- a tab switch, a phone unlocked -- and the listener below re-fetches
// the profile each time. Handing React a NEW object for identical content made
// every screen keyed on `profile` reload: eleven files, the Requests inbox among
// them, which swapped its list for "Loading…" and lost the bell's card it had
// just scrolled to. Measured: seven announcements in twenty seconds, idle.
//
// Identical content keeps the object React already has, so nothing reloads. A
// real change -- an admin switching a permission on -- is different content,
// arrives as a new object, and every screen updates exactly as before. The
// comparison is the whole row as fetched; `select('*')` returns its columns in
// one fixed order, so equal rows serialise equally.
export function keepIfUnchanged(prev, next) {
  if (!prev || !next) return next;
  return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
}

// THE ONE PLACE A ROLE NAME BECOMES THE FLAGS EVERY PERMISSION READS -- the job
// permissions.js's header leaves to this file. Lifted out of the provider at
// Session 79.22b.6.2d (#746) so a test can build a person exactly as the
// running app does: src/lib/weldingRules746.test.js builds every kind of person
// through this rather than through a hand-written copy. `isManager` is true for
// an admin as well, and several rules depend on that, which is exactly what a
// copy can get wrong while every test still passes.
export function roleFlags(profile) {
  const role = profile?.role;
  const isAdmin = role === 'admin';
  const isExecutive = role === 'executive';
  const isManager = role === 'manager' || isAdmin;
  const isTeamMember = role === 'team_member';
  const isPurchasing = role === 'purchasing';
  return { isAdmin, isExecutive, isManager, isTeamMember, isPurchasing, canDoFieldWork: isTeamMember || isManager };
}

// #801, Session 79.22b.6.5.2 -- WHEN A FAILED READ OF WHICH BRANCHES WELD TRIES
// AGAIN. Until now it waited for the next profile read, which comes only when
// the tab is brought back to the front, the sign-in is refreshed, or a setting
// is saved. A welder whose read failed while they stayed on the page -- a phone
// losing signal for a moment on the shop floor -- had Welding missing from the
// menu, the bar and the opening-page choices until they left the app and came
// back, with nothing on screen to say why. The re-test met it once as Owen Pike.
//
// So a failed read books its own next try: 2, 5, 15 and 30 seconds, then once a
// minute until it answers. Welding stays hidden meanwhile, the safe direction.
// One answer ends it, and the read is never repeated after that (746 D5).
//
// ONLY THE RETRY'S OWN FAILURES LENGTHEN THE WAIT. A sign-in reads the profile
// several times in its first second (#327), and each of those reads tries this
// one again; if they all failed on a dead connection and each counted, the
// burst alone would push the next try out to half a minute. A failure that came
// from a profile read books the wait it already had.
export const WELDING_RETRY_DELAYS_MS = [2000, 5000, 15000, 30000, 60000];

// #268's sign-out half, Session 296 -- AN OPEN TAB LEAVES WITHIN ABOUT A MINUTE
// OF ITS PERSON BEING DEACTIVATED (Colby's R1). The profile gate below already
// turns a deactivated person away at sign-in, at every page load and whenever
// the tab comes back into view (the auth library re-announces the sign-in then,
// #686, and the profile is read again). What it could not reach was a tab left
// open and in use: until its sign-in renewed, up to an hour, it kept working.
// So while a tab is in view it asks once a minute whether its person is still
// active -- one column of one row, never the profile read, which would show a
// network blip as "couldn't load your account" every minute.
export const STILL_ACTIVE_CHECK_MS = 60000;

// The answer to that minute's read, kept apart so a test can hold it. A failed
// read is not a denial (#268's decision 2: one blip must not sign the whole
// company out); no row is the gate's 'no_profile'; and inactive is checked as
// `=== false`, the gate's own rule, so an older row's null locks nobody out.
// #268, Session 297: the database's own refusal of a deactivated person is the
// one error that IS an answer, because it now reaches this read first.
export function stillActiveVerdict(row, error) {
  if (isInactiveRefusal(error)) return 'inactive';
  if (error) return null;
  if (!row) return 'no_profile';
  if (row.is_active === false) return 'inactive';
  return null;
}

export function weldingRetryDelay(attempt) {
  const last = WELDING_RETRY_DELAYS_MS.length - 1;
  const n = Math.floor(Number(attempt));
  return WELDING_RETRY_DELAYS_MS[Number.isFinite(n) ? Math.min(Math.max(n, 0), last) : 0];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // #268 — a Supabase session is not authorization. These two carry the
  // outcome of the profile check so App.js can fail closed rather than
  // rendering the shell for a session that has no business being in it.
  const [profileState, setProfileState] = useState('pending'); // 'pending' | 'ok' | 'error'
  const [accessDenied, setAccessDenied] = useState(null);      // null | 'no_profile' | 'inactive'
  // 74z8 — the tenant name, for the topbar's brand block and every printed
  // artifact. Held as state as well as in tenant.js's cache because the two
  // serve different readers: state re-renders the chrome, the cache is what
  // the non-React print utilities read. Both are set from the same fetch.
  const [companyName, setCompanyNameState] = useState(TENANT_FALLBACK);
  // #471, Session 78s.3. Whether the Add-item form proposes a SKU for you.
  // Starts true, matching the column default, so the first render before the
  // settings read lands behaves the way the company configured it rather than
  // the way an empty state would.
  const [skuGenerationEnabled, setSkuGenerationEnabled] = useState(true);
  // #199 part 3, Session 79.22b.6.1f (approved drawing C) -- whether Stede
  // offers QR label printing at all, for stock and tools. Starts true, the
  // column's default, and a failed read leaves it true: the wrong way round
  // costs a Print button that still prints, never a missing one somebody needs.
  const [labelPrintingEnabled, setLabelPrintingEnabled] = useState(true);
  // #199 part 1, Session 79.22b.6.1g (approved drawing D) -- whether using stock
  // needs a WO#. Starts false, the column's default, and a failed read leaves it
  // false: the wrong way round costs one missing WO#, never a person stood at a
  // shelf unable to record what they took.
  const [requireJobRefOnUse, setRequireJobRefOnUse] = useState(false);
  // #746, Session 79.22b.6.2d (746 D5) -- WHICH BRANCHES WELD, for the rule
  // deciding who sees the Welding page (canSeeWelding()). null until the read
  // lands, and while it keeps failing: that hides the page rather than offering
  // it and then refusing (a failed read books its own retry, #801 -- see
  // WELDING_RETRY_DELAYS_MS). Read ONCE per signed-in person, not with every profile
  // read -- the profile is re-read on every tab switch (#686) and up to five
  // times a page load (#327), and this must not join that. The ref keeps the
  // read to one per person whatever StrictMode or the auth library repeats.
  const [weldingBranchIds, setWeldingBranchIds] = useState(null);
  // #746, Session 79.22b.6.3 (746 D5, F4) -- WHERE THAT READ HAS GOT TO, because
  // null above means two different things and the page rule must not confuse
  // them. 'loading' is "not heard yet": a welder whose opening page is Welding
  // is shown it loading rather than being sent to their role's default by a rule
  // that has not heard (deepLink.js, pageRulePending()). 'failed' is "heard, and
  // it failed": the page is hidden from everyone but admins and executives until
  // the retry lands. null means nobody is signed in, 'ready' that ids are known.
  const [weldingBranchesStatus, setWeldingBranchesStatus] = useState(null);
  const weldingRead = useRef({ userId: null, done: false, inFlight: false });
  // #801 -- the retry a failed read books for itself: its timer, and how many
  // booked retries in a row have failed, which picks the delay. Changed in place
  // and never replaced, so the clean-up below always holds the live object.
  const weldingRetry = useRef({ timer: null, attempt: 0 });

  // #373, Session 78g. A Supabase recovery link IS a credential — it establishes
  // a real session — so "set your password" is something the APP has to impose
  // after the fact. It used to be imposed by ROUTE: the flow only worked if the
  // link happened to land on /set-password carrying type=recovery. Anything else
  // and the listener below treated PASSWORD_RECOVERY as an ordinary SIGNED_IN,
  // dropping the user on the landing page, signed in, with no password ever set.
  //
  // That is not a cosmetic miss. An account with no password can only ever be
  // reached by another emailed link, so every new device needs a fresh one from
  // an admin — forever. Across an onboarding cohort that is unworkable, which is
  // why this is now enforced on the EVENT rather than on the URL.
  //
  // Caught two ways on purpose, because neither alone is reliable:
  //   1. The URL marker, read synchronously here — supabase-js may process the
  //      link and emit PASSWORD_RECOVERY before this listener is subscribed, and
  //      a one-shot event that fires early is simply missed.
  //   2. The event itself, for the PKCE/code-exchange shape where the callback
  //      URL carries ?code= and no type=recovery marker at all.
  // SetPasswordPage.js documents the same race from the other side.
  const [passwordRecovery, setPasswordRecovery] = useState(() => {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    return hash.includes('type=recovery') || search.includes('type=recovery');
  });

  // #495, Session 79. WHOSE password the recovery link is for — captured here
  // and nowhere else, because this is the only thing that makes the write safe.
  //
  // What went wrong without it: SetPasswordPage asked "is there a session?" and
  // wrote to whoever it belonged to. A Supabase recovery token is SINGLE-USE,
  // and every corporate mail scanner — Outlook Safe Links among them — fetches
  // links to check them, which spends the token before the person clicks. The
  // link then establishes NO session, `getSession()` returns whatever session
  // was already open in that browser, and the new password lands on THAT
  // account. Colby hit exactly this: a reset sent to `+tm` changed `+dm`.
  //
  // On a shared device that is a silent account takeover, and Stede supports
  // shared devices by design — the admin acceptance pack tests signing out and
  // in as a different person on the same tablet.
  //
  // Captured two ways for the same reason `passwordRecovery` is, and neither
  // alone is enough:
  //   1. Synchronously from the link's own `access_token`, whose `sub` names
  //      the account. This survives the listener race below, where a one-shot
  //      event can fire before anything is subscribed.
  //   2. From the PASSWORD_RECOVERY event, for the PKCE shape where the URL
  //      carries `?code=` and no token at all until it is exchanged.
  //
  // The payload is read WITHOUT verification, deliberately: it is used only to
  // compare against the session Supabase itself established and verified. It
  // grants nothing on its own, so a forged value can only cause a REFUSAL.
  const [recoveryUserId, setRecoveryUserId] = useState(() => {
    try {
      const hash = (window.location.hash || '').replace(/^#/, '');
      const token = new URLSearchParams(hash).get('access_token');
      if (!token) return null;
      const payload = token.split('.')[1];
      if (!payload) return null;
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(json).sub || null;
    } catch {
      // A malformed token is not a crash and not a pass — it simply leaves the
      // identity unknown, which fails closed at the point of use.
      return null;
    }
  });

  // #547, Session 79.14f, gate decision D2. Called by SetPasswordPage the moment
  // verifyOtp() accepts an invite code.
  //
  // WHY THIS SEAM EXISTS, because the obvious assumption is wrong and it fails
  // CLOSED — which means a legitimate new hire being told their link expired.
  // Neither of the two ways `recoveryUserId` is normally captured can see an
  // invite redeemed this way:
  //
  //   1. The synchronous read wants an `access_token` in the fragment. A #547
  //      invite carries a one-time CODE instead, so there is nothing to decode.
  //   2. The listener wants a PASSWORD_RECOVERY event. `verifyOtp()` emits
  //      SIGNED_IN.
  //
  // So without this the identity stays null, #495's guard refuses, and the
  // person sees "Link expired" at the exact moment everything worked.
  //
  // AND THE VALUE IS BETTER EVIDENCE THAN THE ONE IT STANDS IN FOR, which is
  // worth stating because it looks like a weakening. The synchronous read
  // decodes an UNVERIFIED token payload and is trusted only because it can
  // cause a refusal. This id comes from a code Supabase itself just verified
  // SERVER-SIDE, and verifyOtp returns an error rather than a session when the
  // code is spent. It cannot name an account the code was not issued for.
  //
  // #495's comparison still runs afterwards, unchanged, as a second layer.
  function noteRecoveryUser(userId) {
    if (!userId) return;
    setPasswordRecovery(true);
    setRecoveryUserId(userId);
  }

  // Called by SetPasswordPage once updateUser() has actually succeeded. Not
  // cleared on navigation: leaving it set is what stops the requirement being
  // dismissed by tapping away from the screen.
  function clearPasswordRecovery() {
    setPasswordRecovery(false);
    // #495 — cleared with the flag. Leaving a stale intended-recipient behind
    // would let the NEXT visit to the page match against an account this one
    // finished with.
    setRecoveryUserId(null);
  }

  // #746 -- forgotten whenever nobody is signed in, so the next person's list
  // is read afresh rather than inherited on a shared device.
  function forgetWeldingBranches() {
    weldingRead.current = { userId: null, done: false, inFlight: false };
    // #801 -- a retry booked for the person who has gone goes with them.
    clearTimeout(weldingRetry.current.timer);
    weldingRetry.current.timer = null;
    weldingRetry.current.attempt = 0;
    setWeldingBranchIds(null);
    setWeldingBranchesStatus(null);
  }

  // #268 — signs the session straight back out and records why, so Login
  // can say something useful instead of silently returning to a blank form.
  async function denyAccess(reason) {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    forgetWeldingBranches();
    setProfileState('ok');
    setAccessDenied(reason);
  }

  // #746, 746 D5 -- every branch that makes an active welded size. Called only
  // once the profile check has let the person in, and not awaited: like the
  // company name, it must never delay deciding whether this session may enter.
  // welding_branches() runs for signed-in people only (its migration's (e)).
  async function fetchWeldingBranches(userId, fromRetry = false) {
    const r = weldingRead.current;
    if (r.userId === userId && (r.done || r.inFlight)) return;
    // #801 -- this read is the booked retry or arrives ahead of it, so nothing
    // stays booked behind it. A different person starts from the first delay.
    clearTimeout(weldingRetry.current.timer);
    weldingRetry.current.timer = null;
    if (r.userId !== userId) weldingRetry.current.attempt = 0;
    weldingRead.current = { userId, done: false, inFlight: true };
    // Set before the read leaves, in the same turn as the profile it follows, so
    // no render ever holds a profile with this still unset.
    setWeldingBranchesStatus('loading');
    // #801 -- a read that throws instead of answering has failed too. Left
    // uncaught it would stay on its way for good, where no retry can reach it.
    let data = null;
    let error = null;
    try {
      ({ data, error } = await supabase.rpc('welding_branches'));
    } catch (thrown) {
      error = thrown || new Error('welding_branches did not answer');
    }
    // Signed out, or somebody else signed in, while it was on its way: drop it.
    if (weldingRead.current.userId !== userId) return;
    if (error || !Array.isArray(data)) {
      // Not marked done, so the next profile read tries again -- and so does the
      // retry booked here (#801), which does not wait for one. Until a read
      // answers, the page stays hidden -- the safe direction.
      weldingRead.current = { userId, done: false, inFlight: false };
      setWeldingBranchesStatus('failed');
      if (fromRetry) weldingRetry.current.attempt += 1;
      weldingRetry.current.timer = setTimeout(() => {
        weldingRetry.current.timer = null;
        if (weldingRead.current.userId === userId) fetchWeldingBranches(userId, true);
      }, weldingRetryDelay(weldingRetry.current.attempt));
      return;
    }
    weldingRetry.current.attempt = 0;
    weldingRead.current = { userId, done: true, inFlight: false };
    const ids = data.map(Number).filter(Number.isFinite);
    setWeldingBranchIds(prev => keepIfUnchanged(prev, ids));
    setWeldingBranchesStatus('ready');
  }

  async function fetchProfile(userId) {
    if (!userId) return;
    setProfileState('pending');
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    // #268, Session 297 — the database refused this read because the person is
    // deactivated, so the row below never arrives. It is the same answer as
    // is_active === false, and must not fall into "couldn't load your account".
    if (isInactiveRefusal(error)) {
      await denyAccess('inactive');
      return;
    }

    // A transient PostgREST/network failure must not read as "no profile" —
    // denying here would turn one blip into a company-wide lockout. Surfaced
    // with a retry instead of an indefinite spinner, same treatment #96 gave
    // SetPasswordPage's getSession() failure.
    if (error) {
      setProfile(null);
      setProfileState('error');
      return;
    }

    // No public.users row: a session exists but the person was never
    // provisioned through User Management. Nothing in the app creates one,
    // so this is not a state a legitimate user can reach.
    if (!data) {
      await denyAccess('no_profile');
      return;
    }

    // Deactivated. Checked as === false rather than falsy on purpose: a null
    // is_active on an older row is not a deactivation and must not lock
    // anyone out. toggleActive() writes an explicit boolean.
    if (data.is_active === false) {
      await denyAccess('inactive');
      return;
    }

    data.branches = data.branch_name ? { name: data.branch_name } : null;
    setProfile(prev => keepIfUnchanged(prev, data));
    setProfileState('ok');
    // Deliberately not awaited. The tenant name is chrome, and blocking the
    // profile gate on it would turn a cosmetic fetch into an access-control
    // dependency — a slow settings read must never delay deciding whether
    // this session is allowed in. Until it lands, the fallback renders.
    fetchCompanySettings();
    // #746 -- not awaited either, for the same reason; once per person.
    fetchWeldingBranches(userId);
  }

  // 74z8. One row, read once per session. `company_settings`' SELECT policy
  // is roles={authenticated}, so this cannot run before the profile check —
  // which is why it is called from fetchProfile rather than from initAuth.
  async function fetchCompanySettings() {
    // Guarded in tenant.js rather than here — see loadCompanyNameOnce(). This
    // function is reached from both initAuth and onAuthStateChange, and both
    // are double-invoked by StrictMode in development.
    const name = await loadCompanyNameOnce(async () => {
      // #342, Session 78c: print_on_receive_default rides this select rather
      // than getting a query of its own. One row, read once per session, and
      // this file's own caching guard exists because duplicate reads of it were
      // measured at ~100ms each on the sign-in path. A second column is free.
      //
      // #471, Session 78s.3: sku_generation_enabled rides it for the same
      // reason, and for a second one. The Add-item form is rendered from FOUR
      // call sites across two files; passing the setting down as a prop means
      // four chances to miss one, and a missed one silently stops generating
      // codes on that screen alone. Reading it here makes that impossible.
      //
      // NOTE, and it is the push gate: PostgREST fails the WHOLE query on an
      // unknown column rather than degrading, so adding this name here breaks
      // sign-in everywhere on any database that lacks the column. Both have it
      // as of Sep 4, 2026 -- db/migrations/20260904_sku_generation_category_codes.sql,
      // statement 7, applied to staging and production.
      //
      // #199 part 3, Session 79.22b.6.1f: qr_label_printing_enabled rides it
      // for the #471 reason -- LabelCard alone renders on four screens. The
      // column predates the migrations folder and was read live on BOTH
      // databases at this session's open, so the push gate above is met.
      // #199 part 1, Session 79.22b.6.1g: require_job_ref_on_consume likewise --
      // read live on both databases at 79.22b.6.1f's open.
      const { data, error } = await supabase
        .from('company_settings')
        .select('company_name, print_on_receive_default, sku_generation_enabled, qr_label_printing_enabled, require_job_ref_on_consume')
        .maybeSingle();
      // A failure here is not worth surfacing: the fallback is the real
      // company's name today, so the visible outcome is nothing at all.
      if (error) return null;
      // Latched inside the loader deliberately. loadCompanyNameOnce only marks
      // itself loaded on a truthy NAME, so doing this here means the setting
      // still lands on a row whose company_name is somehow blank — the two
      // values fail independently rather than one taking the other down.
      setPrintOnReceiveDefault(data?.print_on_receive_default);
      // Defaults to ON when the read gives us nothing, matching the column's
      // own default. The two ways of being wrong are not equally bad: a code
      // suggested when it should not have been is a value somebody clears in
      // one keystroke, while a code NOT suggested when it should have been is
      // an empty box somebody has to invent a value for, standing at a shelf.
      setSkuGenerationEnabled(data?.sku_generation_enabled !== false);
      setLabelPrintingEnabled(data?.qr_label_printing_enabled !== false);
      setRequireJobRefOnUse(data?.require_job_ref_on_consume === true);
      return data?.company_name || null;
    });
    setCompanyNameState(name);
  }

  useEffect(() => {
    async function initAuth() {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // A stale or revoked refresh token means this session is dead. Carrying
        // its user forward would put every later query on a token PostgREST
        // rejects, which #268's new profile gate would then report as
        // "couldn't load your account" — a retry prompt for something no retry
        // can fix. Clear it and show Login, which is the honest answer.
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          await supabase.auth.signOut();
          setUser(null);
          setProfile(null);
          setProfileState('ok');
          setLoading(false);
          return;
        }
      }
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser?.id) {
        // Awaited so `loading` does not clear until the decision is made —
        // otherwise a denied session flashes the app shell before the
        // sign-out lands.
        await fetchProfile(currentUser.id);
      } else {
        setProfileState('ok');
      }
      setLoading(false);
    }

    initAuth();

    // Fires on SIGNED_IN, SIGNED_OUT and TOKEN_REFRESHED. The refresh case
    // matters for #268: an already-open tab belonging to a since-deactivated
    // user is re-checked on its next token refresh rather than surviving
    // until the page is reloaded.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // #373 — the event was discarded as `_event` until Session 78g, which is
      // exactly why a recovery link read as an ordinary sign-in.
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
        // #495 — the event's own session names the account the link was issued
        // for. Recorded even when the synchronous read above already found it,
        // because the PKCE shape has no token in the URL to read.
        if (session?.user?.id) setRecoveryUserId(session.user.id);
      }
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser?.id) {
        fetchProfile(currentUser.id);
      } else {
        setProfile(null);
        forgetWeldingBranches();
        setProfileState('ok');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // #801 -- a booked retry ends with the provider rather than firing into it.
  // The ref's object is only ever changed in place, so this holds the live one.
  useEffect(() => {
    const retry = weldingRetry.current;
    return () => clearTimeout(retry.timer);
  }, []);

  // #268's sign-out half, Session 296 -- the minute check (STILL_ACTIVE_CHECK_MS).
  // Runs only for a person the gate has let in, and only while the tab is in
  // view: a hidden tab is checked the moment it comes back, by the profile read
  // the auth library's re-announcement already triggers. A deactivated person's
  // sign-in is also locked at Supabase now, so their tab cannot renew either.
  const admittedUserId = profileState === 'ok' && profile ? user?.id : null;
  useEffect(() => {
    if (!admittedUserId) return undefined;
    let ended = false;
    const timer = setInterval(async () => {
      if (document.visibilityState === 'hidden') return;
      const { data, error } = await supabase
        .from('users')
        .select('is_active')
        .eq('id', admittedUserId)
        .maybeSingle();
      if (ended) return;
      const verdict = stillActiveVerdict(data, error);
      if (verdict) await denyAccess(verdict);
    }, STILL_ACTIVE_CHECK_MS);
    return () => { ended = true; clearInterval(timer); };
    // denyAccess only calls setters and the auth library, so it needs no place
    // here; the check restarts whenever a different person is let in.
  }, [admittedUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  function retryProfile() {
    if (user?.id) fetchProfile(user.id);
  }

  async function signIn(email, password) {
    // Clear any previous refusal so the message belongs to this attempt.
    setAccessDenied(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }

  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    forgetWeldingBranches();
    setAccessDenied(null);
    setProfileState('ok');
    // #373 — a signed-out session has no password to set. Leaving this stuck
    // would pin the next visitor on this browser to the set-password screen.
    setPasswordRecovery(false);
    // Clear the tenant so a second sign-in on a shared machine cannot briefly
    // show the previous tenant's name in the chrome before its own fetch lands.
    resetCompanyName();
    setCompanyNameState(TENANT_FALLBACK);
  }

  const { isAdmin, isExecutive, isManager, isTeamMember, isPurchasing, canDoFieldWork } = roleFlags(profile);

  return (
  <AuthContext.Provider value={{
  user, profile, loading, signIn, signOut, companyName,
  skuGenerationEnabled,
  // #199 part 3 -- read once at sign-in; Company settings calls the setter
  // after its own save, so the person flipping the switch sees it at once (C4).
  labelPrintingEnabled, setLabelPrintingEnabled,
  // #199 part 1 -- the same pattern: read at sign-in, set at once by the saver.
  requireJobRefOnUse, setRequireJobRefOnUse,
  // #746 -- which branches weld; null until known. canSeeWelding() reads it.
  weldingBranchIds,
  // #746 -- and whether that read is still on its way, done, or failed.
  weldingBranchesStatus,
  profileState, accessDenied, retryProfile,
  passwordRecovery, clearPasswordRecovery, recoveryUserId, noteRecoveryUser,
  isAdmin, isExecutive, isManager, isTeamMember, isPurchasing, canDoFieldWork
}}>
    {children}
  </AuthContext.Provider>
);
}

export const useAuth = () => useContext(AuthContext);
